import { FC, ReactNode } from "react";
import { observer } from "mobx-react";
import {
  getNoticeOfBoardMeetingCustomPanels,
  NoticeOfBoardMeetingCustomPanel,
  NoticeOfBoardMeetingForm,
} from "./noticeBoardMeetingDomain";
import {
  CollapseList,
  DateQuestion,
  DecisionTextsView,
  DetailsSummaryList,
  DocumentLanguageSelector,
  DocumentViewingStatusLine,
  LocalDateTimePicker,
  NoticeDocumentAuthStatusLine,
} from "@/dbot/common/commonComponents";
import { Button, IconButton, LinkButton } from "@/components-v2/Button";
import { Checkbox, MCheckboxGroup } from "@/components-v2/Checkbox";
import { Box, VBox } from "@/components-v2/Box";
import { MTextInput, TextInput } from "@/components-v2/TextInput";
import { DetailsPanel, getDecisionDetailsPanels } from "@/dbot/common/commonDomain";
import { AgendaActions } from "@/dbot/common/commonAgendaComponents";
import { AttachmentsPanel } from "@/components-v2/AttachmentsPanel";
import { when } from "@/utils/functions";
import { DecisionType } from "@/common/decisions";
import { useTranslation } from "@/stores/RootStoreContext";
import { AddNameAndEmailForm, RadioFieldQuestion } from "@/common/commonComponents";
import { LocalizedTextEditor } from "@/text/LocalizedTextEditor";
import { disabledSendNoticeInfo } from "@/dbot/summary/summaryDomain";
import { Alert } from "@/components-v2/Alert";
import {
  prepDocumentAttachmentsPanel,
  prepUploadDocumentAttachmentsButton,
} from "@/dbot/attachments";
import {
  CommonBoardMeetingCustomPanel,
  getNoticeToGmPanels,
} from "../common/commonBoardMeetingDomain";
import { NoticeToGmPanel } from "../common/commonBoardMeetingComponents";

const ExtraParticipants: FC<{ form: NoticeOfBoardMeetingForm }> = observer(({ form }) => {
  const { t } = useTranslation();

  return (
    <VBox>
      <Box>
        <div>{t("documents.noticeBoardMeeting.extraRecipients")}</div>
      </Box>
      {form.extraParticipants.map((p) => (
        <Box key={p.id} alignItems={"center"} marginLeft={"md"} justifyContent={"space-between"}>
          <div>
            {p.name} ({p.email})
          </div>
          <IconButton
            icon={"trash"}
            color={"error"}
            tooltip={t("remove")}
            onClick={() => form.removeExtraParticipant(p)}
            disabled={form.isLocked}
            compact
          />
        </Box>
      ))}

      <Box>
        {!form.isLocked && form.extraParticipantFormModel ? (
          <AddNameAndEmailForm
            form={form.extraParticipantFormModel}
            submit={form.submitExtraParticipant}
            cancel={form.cancelAddExtraParticipant}
          />
        ) : (
          <LinkButton
            label={t("documents.noticeBoardMeeting.addRecipient")}
            icon={"plus"}
            onClick={form.addExtraParticipant}
            disabled={form.isLocked}
          />
        )}
      </Box>
    </VBox>
  );
});

const SelectBoardMembers: FC<{ form: NoticeOfBoardMeetingForm }> = observer(({ form }) => {
  const { t } = useTranslation();
  return form.selectedBoardMembers.options.length === 1 ? (
    <MCheckboxGroup model={form.selectedBoardMembers} disabled={form.isLocked} />
  ) : (
    <CollapseList
      closeLabel={t("collapse")}
      openLabel={t("dbot.showAll")}
      initiallyExpanded={!form.selectedBoardMembers.isAllSelected}
      togglePosition={"start"}
      renderToggle={(toggleCollapse, label, isCollapsed) => (
        <Box justifyContent={"space-between"}>
          <Checkbox
            label={{
              key: "dbot.boardmeeting.selectBoardMembers",
              params: { n: form.selectedBoardMembers.options.length },
            }}
            onChange={(event) => {
              form.selectedBoardMembers.toggleAll(event.target.checked);
              if (isCollapsed && !event.target.checked) {
                toggleCollapse();
              }
            }}
            checked={form.selectedBoardMembers.isAllSelected}
            disabled={form.isLocked}
          />
          <LinkButton label={label} onClick={toggleCollapse} />
        </Box>
      )}
    >
      <VBox paddingLeft={"lg"}>
        <MCheckboxGroup model={form.selectedBoardMembers} disabled={form.isLocked} />
      </VBox>
    </CollapseList>
  );
});

const SelectCeos: FC<{ form: NoticeOfBoardMeetingForm }> = observer(({ form }) => {
  const { t } = useTranslation();
  return form.selectedCeos.options.length === 1 ? (
    <MCheckboxGroup
      model={form.selectedCeos}
      labelFn={(item) => t("dbot.boardmeeting.ceoOptionLabel", { name: item.name })}
      disabled={form.isLocked}
    />
  ) : (
    <CollapseList
      closeLabel={t("collapse")}
      openLabel={t("dbot.showAll")}
      initiallyExpanded={!form.selectedCeos.isAllSelected}
      togglePosition={"start"}
      renderToggle={(toggleCollapse, label) => (
        <Box justifyContent={"space-between"}>
          <Checkbox
            label={{
              key: "dbot.boardmeeting.selectCeos",
              params: { n: form.selectedCeos.options.length },
            }}
            onChange={(event) => form.selectedCeos.toggleAll(event.target.checked)}
            checked={form.selectedCeos.isAllSelected}
            disabled={form.isLocked}
          />
          <LinkButton label={label} onClick={toggleCollapse} />
        </Box>
      )}
    >
      <VBox paddingLeft={"lg"}>
        <MCheckboxGroup model={form.selectedCeos} disabled={form.isLocked} />
      </VBox>
    </CollapseList>
  );
});

const SelectDeputyMembers: FC<{ form: NoticeOfBoardMeetingForm }> = observer(({ form }) => {
  const { t } = useTranslation();
  return form.selectedDeputyMembers.options.length === 1 ? (
    <MCheckboxGroup
      model={form.selectedDeputyMembers}
      labelFn={(item) => t("dbot.boardmeeting.deputyBoardMemberOptionLabel", { name: item.name })}
      disabled={form.isLocked}
    />
  ) : (
    <CollapseList
      closeLabel={t("collapse")}
      openLabel={t("dbot.showAll")}
      initiallyExpanded={
        !form.selectedDeputyMembers.isAllSelected && !form.selectedDeputyMembers.isNoneSelected
      }
      togglePosition={"start"}
      renderToggle={(toggleCollapse, label) => (
        <Box justifyContent={"space-between"}>
          <Checkbox
            label={{
              key: "dbot.boardmeeting.selectDeputyMembers",
              params: { n: form.selectedDeputyMembers.options.length },
            }}
            onChange={(event) => form.selectedDeputyMembers.toggleAll(event.target.checked)}
            checked={form.selectedDeputyMembers.isAllSelected}
            disabled={form.isLocked}
          />
          <LinkButton label={label} onClick={toggleCollapse} />
        </Box>
      )}
    >
      <VBox paddingLeft={"lg"}>
        <MCheckboxGroup model={form.selectedDeputyMembers} disabled={form.isLocked} />
      </VBox>
    </CollapseList>
  );
});

const SelectObservers: FC<{ form: NoticeOfBoardMeetingForm }> = observer(({ form }) => {
  const { t } = useTranslation();
  return form.selectedObservers.options.length === 1 ? (
    <MCheckboxGroup
      model={form.selectedObservers}
      labelFn={(item) => t("dbot.boardmeeting.observerOptionLabel", { name: item.name })}
      disabled={form.isLocked}
    />
  ) : (
    <CollapseList
      closeLabel={t("collapse")}
      openLabel={t("dbot.showAll")}
      initiallyExpanded={
        !form.selectedObservers.isAllSelected && !form.selectedObservers.isNoneSelected
      }
      togglePosition={"start"}
      renderToggle={(toggleCollapse, label) => (
        <Box justifyContent={"space-between"}>
          <Checkbox
            label={{
              key: "dbot.boardmeeting.selectObservers",
              params: { n: form.selectedObservers.options.length },
            }}
            onChange={(event) => form.selectedObservers.toggleAll(event.target.checked)}
            checked={form.selectedObservers.isAllSelected}
            disabled={form.isLocked}
          />
          <LinkButton label={label} onClick={toggleCollapse} />
        </Box>
      )}
    >
      <VBox paddingLeft={"lg"}>
        <MCheckboxGroup model={form.selectedObservers} disabled={form.isLocked} />
      </VBox>
    </CollapseList>
  );
});

const MeetingForm = observer(({ form }: { form: NoticeOfBoardMeetingForm }) => {
  const { t } = useTranslation();

  return (
    <VBox gap={"md"}>
      <RadioFieldQuestion
        field={form.meetingForm}
        fieldName={"meetingForm"}
        question={{ key: "documents.meetingform" }}
        row
        isLocked={form.isLocked}
      />
      {form.showAddress && (
        <RadioFieldQuestion
          field={form.meetingPlace}
          fieldName={"meetingPlace"}
          question={{ key: "documents.noticeBoardMeeting.noticeToBoardMeetingPlace" }}
          row
          isLocked={form.isLocked}
        />
      )}
      {form.showInlineAddress && (
        <TextInput
          label={t("documents.meetingForm.address")}
          value={form.inlineAddress.inputValue}
          onChange={form.inlineAddress.setInputValue}
          onBlur={form.inlineAddress.commit}
          readOnly={form.isLocked}
        />
      )}
      {form.showElectronicLink && (
        <RadioFieldQuestion
          field={form.meetingLinkOption}
          fieldName={"meetingLinkOption"}
          question={{ key: "documents.meetingForm.forElectronicMeetings" }}
          row
          isLocked={form.isLocked}
        />
      )}
      {form.showElectronicLinkInput && (
        <TextInput
          label={t("documents.meetingForm.inlineAddress")}
          value={form.inlineMeetingLink.inputValue}
          onChange={form.inlineMeetingLink.setInputValue}
          onBlur={form.inlineMeetingLink.commit}
          readOnly={form.isLocked}
        />
      )}
    </VBox>
  );
});

export const NoticeOfBoardMeetingAgendaItemDetails = observer(
  function NoticeOfBoardMeetingAgendaItemDetails({
    form,
    decisionId,
    renderDecisionForm,
    renderDecisionAttachment,
  }: {
    form: NoticeOfBoardMeetingForm;
    decisionId: string;
    renderDecisionForm: (decisionId: string) => ReactNode;
    renderDecisionAttachment: (decisionId: string) => ReactNode;
  }) {
    const decision = form.currentDecisions.find((d) => d.decisionId === decisionId);
    if (!decision) {
      return null;
    }

    const editor = form.decisionTextModels.find((d) => d.decisionId === decisionId);
    return (
      <VBox gap={"md"}>
        {renderDecisionForm(decisionId)}
        {decision.type !== DecisionType.freetext && editor && (
          <DecisionTextsView form={editor} isLocked={form.isLocked} hideIntro />
        )}
        {renderDecisionAttachment(decisionId)}
      </VBox>
    );
  },
);

export const NoticeOfBoardMeetingDetails = observer(function NoticeOfBoardMeetingDetails({
  form,
}: {
  form: NoticeOfBoardMeetingForm;
}) {
  const { t } = useTranslation();

  return (
    <VBox gap={"md"}>
      <DocumentLanguageSelector field={form.documentLanguage} isLocked={form.isLocked} />
      <MTextInput
        name={"boardMeetingNumber"}
        label={t("documents.noticeBoardMeeting.boardMeetingNumberHelperText")}
        prefix={"#"}
        model={form.boardMeetingNumber}
        readOnly={form.isLocked}
      />
      <DateQuestion
        question={t("documents.noticeBoardMeeting.noticeDate")}
        field={form.dateNoticeBoardMeeting}
        isLocked={form.isLocked}
      />
      <VBox>
        <div>{t("documents.noticeBoardMeeting.dateBoardMeetingHelperText")}</div>
        <LocalDateTimePicker
          dateLabel={{ key: "documents.noticeBoardMeeting.date" }}
          dateField={form.meetingStartDate}
          timeLabel={{ key: "documents.noticeBoardMeeting.startTime" }}
          timeField={form.meetingStartTime}
          endTimeLabel={{ key: "documents.noticeBoardMeeting.endTime" }}
          endTimeField={form.meetingEndTime}
          isLocked={form.isLocked}
        />
      </VBox>
      <MeetingForm form={form} />
      <LocalizedTextEditor model={form.noticeDescriptionModel} />
    </VBox>
  );
});

export const NoticeOfBoardMeetingRecipients = observer(function NoticeOfBoardMeetingRecipients({
  form,
}: {
  form: NoticeOfBoardMeetingForm;
}) {
  const { t } = useTranslation();
  return (
    <VBox gap={"lg"}>
      <VBox>
        <div>{t("documents.noticeBoardMeeting.selectRecipients")}</div>
        <SelectBoardMembers form={form} />
        {form.hasCeos && <SelectCeos form={form} />}
        {form.hasDeputyBoardMembers && <SelectDeputyMembers form={form} />}
        {form.hasObservers && <SelectObservers form={form} />}
      </VBox>
      <ExtraParticipants form={form} />
    </VBox>
  );
});

export const NoticeOfBoardMeetingDetailsPanel = observer(function NoticeOfBoardMeetingDetailsPanel({
  panel,
  form,
  renderDecisionForm,
  renderDecisionAttachment,
}: {
  panel: DetailsPanel;
  form: NoticeOfBoardMeetingForm;
  renderDecisionForm: (decisionId: string) => ReactNode;
  renderDecisionAttachment: (decisionId: string) => ReactNode;
}) {
  switch (panel.type) {
    case "decision":
      return (
        <NoticeOfBoardMeetingAgendaItemDetails
          form={form}
          decisionId={panel.id}
          renderDecisionForm={renderDecisionForm}
          renderDecisionAttachment={renderDecisionAttachment}
        />
      );
    case "custom":
      switch (panel.id) {
        case NoticeOfBoardMeetingCustomPanel.meetingDetails:
          return <NoticeOfBoardMeetingDetails form={form} />;
        case NoticeOfBoardMeetingCustomPanel.recipients:
          return <NoticeOfBoardMeetingRecipients form={form} />;
        case CommonBoardMeetingCustomPanel.noticeToGm:
          return <NoticeToGmPanel />;
      }
  }
  return null;
});

export const NoticeOfBoardMeetingView = observer(function NoticeOfBoardMeetingView({
  form,
}: {
  form: NoticeOfBoardMeetingForm;
}) {
  const { t } = useTranslation();
  return (
    <VBox gap={"md"}>
      <DocumentViewingStatusLine context={form.context} dispatch={form.dispatch} />
      <NoticeDocumentAuthStatusLine context={form.context} />
      <DetailsSummaryList
        dispatch={form.dispatch}
        panels={getNoticeOfBoardMeetingCustomPanels(form)}
        isLocked={form.isLocked}
      />
      <VBox gap={"xs"}>
        <DetailsSummaryList
          dispatch={form.dispatch}
          panels={getDecisionDetailsPanels(form)}
          isLocked={form.isLocked}
        />
        <DetailsSummaryList
          dispatch={form.dispatch}
          panels={getNoticeToGmPanels(form.context)}
          isLocked={form.isLocked}
        />
      </VBox>
      <AgendaActions
        isLocked={form.isLocked}
        context={form.context}
        dispatch={form.dispatch}
        uploadAttachments={prepUploadDocumentAttachmentsButton(form.context, form.dispatch)}
      />
      {when(prepDocumentAttachmentsPanel(form.context, form.dispatch), (props) => (
        <AttachmentsPanel {...props} />
      ))}
      {when(form.openSendNoticeDialogAction, (action) => (
        <>
          {form.isNoticeSent && (
            <Alert
              icon
              type="warning"
              message={{
                key: "dbot.send-notice.new-recipients.info",
                params: { count: form.numberOfNewRecipients },
              }}
            />
          )}
          <Button
            variant={"primary"}
            label={t(action.label)}
            onClick={() => form.dispatch(action)}
          />
        </>
      ))}
      {when(form.openNotifyNoticeChangeDialogAction, (action) => (
        <Button
          variant={"secondary"}
          label={t(action.label)}
          onClick={() => form.dispatch(action)}
        />
      ))}
      {when(disabledSendNoticeInfo(form.context), (msg) => (
        <Alert type="info" message={msg} />
      ))}
    </VBox>
  );
});
